import { useEffect, useRef, useState } from 'react'
import { RefreshCw, X } from 'lucide-react'

import { appVersion, fetchServedAppVersion } from '@/lib/buildInfo'

const CHECK_INTERVAL_MS = 5 * 60_000
const RELOAD_COUNTDOWN_SECONDS = 30

// Staff browser tabs live for days, so a deploy leaves them on stale code
// (the kiosk tablet handles this itself with an idle-time auto-reload).
// Here a human is mid-work, so announce the reload and let them cancel it;
// a cancelled version is not offered again until the next deploy.
export function AppUpdateNotifier() {
  const [pendingVersion, setPendingVersion] = useState<string | null>(null)
  const [secondsLeft, setSecondsLeft] = useState(RELOAD_COUNTDOWN_SECONDS)
  const dismissedVersionRef = useRef<string | null>(null)

  useEffect(() => {
    if (!appVersion) return

    const check = async () => {
      const servedVersion = await fetchServedAppVersion()
      if (!servedVersion || servedVersion === appVersion) return
      if (dismissedVersionRef.current === servedVersion) return
      setPendingVersion((current) => current ?? servedVersion)
    }

    const interval = window.setInterval(() => {
      void check()
    }, CHECK_INTERVAL_MS)

    return () => window.clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!pendingVersion) return

    setSecondsLeft(RELOAD_COUNTDOWN_SECONDS)
    const tick = window.setInterval(() => {
      setSecondsLeft((seconds) => seconds - 1)
    }, 1000)

    return () => window.clearInterval(tick)
  }, [pendingVersion])

  useEffect(() => {
    if (!pendingVersion || secondsLeft > 0) return
    window.location.reload()
  }, [pendingVersion, secondsLeft])

  if (!pendingVersion) return null

  const cancel = () => {
    dismissedVersionRef.current = pendingVersion
    setPendingVersion(null)
  }

  return (
    <output
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 block w-80 max-w-[calc(100vw-2rem)] animate-in fade-in slide-in-from-bottom-4"
    >
      <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-lg">
        <div className="mt-0.5 rounded-full bg-blue-100 p-1.5 text-blue-600">
          <RefreshCw className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900">
            Update available ({pendingVersion})
          </p>
          <p className="mt-0.5 text-sm text-slate-500">
            Reloading in {Math.max(secondsLeft, 0)}s to apply it.
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              className="cursor-pointer rounded bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700"
              onClick={() => window.location.reload()}
            >
              Reload now
            </button>
            <button
              type="button"
              className="cursor-pointer rounded border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
              onClick={cancel}
            >
              Cancel
            </button>
          </div>
        </div>
        <button
          type="button"
          aria-label="Cancel reload"
          className="cursor-pointer rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          onClick={cancel}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </output>
  )
}
